import { Box, Card, TextField, Typography } from '@mui/material';
import { InputBaseComponentProps } from '@mui/material/InputBase';
import { forwardRef, ReactNode } from 'react';

type FormFieldsetProps = {
  legend: string;
  hasError?: boolean;
  fullHeight?: boolean;
  children: JSX.Element;
  childrenTop?: boolean;
  noPadding?: boolean;
  displayInline?: boolean;
  // Renderar innehållet i "input"-slotten på en RIKTIG MUI outlined-input (TextField), så fältet blir
  // EXAKT identiskt med övriga outlined-fält (Betalmetod, Distrikt/Län/Förening): samma ram, notch,
  // flytande label och vita bakgrund. Default (false) behåller den absolut-positionerade legenden med vit
  // maskerings-bakgrund (oförändrat för de övriga 21 användningarna av FormFieldset).
  inflowLegend?: boolean;
};

// "Input"-komponent som renderar godtyckligt innehåll (t.ex. en toggle-grupp) inuti MUI:s OutlinedInput.
// Läser innehållet via `contentnode` och sprider INTE MUI:s övriga input-props till DOM (undviker varningar).
const OutlinedContentSlot = forwardRef<HTMLDivElement, InputBaseComponentProps & { contentnode?: ReactNode }>(function OutlinedContentSlot(
  { className, contentnode },
  ref,
) {
  return (
    <Box ref={ref} className={className} sx={{ display: 'flex', alignItems: 'center', minHeight: '40px' }}>
      {contentnode}
    </Box>
  );
});

export const FormFieldset = (props: FormFieldsetProps) => {
  const {
    legend,
    hasError = false,
    fullHeight = false,
    children,
    childrenTop = false,
    noPadding = false,
    displayInline = false,
    inflowLegend = false,
  } = props;

  if (inflowLegend) {
    return (
      <TextField
        label={legend}
        size='small'
        fullWidth={!displayInline}
        error={hasError}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          inputComponent: OutlinedContentSlot,
          inputProps: { contentnode: children } as unknown as InputBaseComponentProps,
          // Nollställ vertikal padding (innehållets egen 40px-höjd styr), men behåll horisontell padding
          // så toggle-gruppen får samma vänster-avstånd som övriga outlined-fälts text.
          sx: {
            '& .MuiOutlinedInput-input': { py: 0, pl: '2px', pr: '4px', height: 'auto' },
            // Knapparnas line-height (StyledToggleButtonGroup sätter 1.25 direkt på .grouped) styr
            // toggle-fältets vertikala rytm; sätt 1.35 så det centreras rätt i inputen.
            '& .MuiToggleButtonGroup-grouped': { lineHeight: 1.35 },
          },
        }}
      />
    );
  }

  return (
    <Card
      variant='outlined'
      sx={{
        display: displayInline ? 'inline-block' : 'block',
        p: noPadding ? 0 : 0.5,
        m: 0,
        position: 'relative',
        overflow: 'visible',
        borderColor: (thm) => (hasError ? `${thm.palette.error.main} !important` : thm.palette.divider),
      }}
      component='fieldset'
    >
      <Typography
        component='legend'
        variant='caption'
        sx={{
          position: 'absolute',
          left: 5,
          top: -4,
          ml: 0.5,
          px: '5px',
          zIndex: 500,
          backgroundColor: '#fff',
          color: (thm) => (hasError ? thm.palette.error.light : 'rgba(0,0,0,0.6)'),
          lineHeight: 0.5,
        }}
      >
        {legend}
      </Typography>
      <Box
        sx={{
          ...(fullHeight && { height: '100%', display: 'flex', justifyContent: 'center', alignItems: childrenTop ? 'flex-start' : 'center' }),
        }}
      >
        {children}
      </Box>
    </Card>
  );
};
